import React, { useState, useEffect, useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useTheme, useMediaQuery } from '@mui/material';

const OnboardingTour = () => {
  const [run, setRun] = useState(false);
  const [tourSettings, setTourSettings] = useState({
    enabled: false,
    routes: {
      dashboard: false,
      workspace: false,
      connections: false,
      reports: false,
      pipeline: false,
      help: false,
      admin: false,
    },
    isFirstTime: true,
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const getContentStyles = () => {
    if (isMobile) {
      return {
        title: { margin: '0 0 10px 0', fontSize: '18px', fontWeight: 600 },
        text: { margin: 0, fontSize: '15px', lineHeight: '1.6' },
      };
    }
    if (isTablet) {
      return {
        title: { margin: '0 0 7px 0', fontSize: '17px', fontWeight: 600 },
        text: { margin: 0, fontSize: '13.5px', lineHeight: '1.5' },
      };
    }
    return {
      title: { margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 },
      text: { margin: 0, fontSize: '14px', lineHeight: '1.5' },
    };
  };

  const contentStyles = getContentStyles();

  const steps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to DataVerse! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s take a quick tour to help you get started with our ETL platform.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="dashboard-cards"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Quick Actions
          </h3>
          <p style={contentStyles.text}>
            Start here! Create pipelines, monitor runs, manage connections, or browse templates.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-tour="data-sources"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Data Sources
          </h3>
          <p style={contentStyles.text}>
            Explore available connectors for SaaS APIs, databases, streaming, and file storage.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ], [contentStyles]);

  const workspaceSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Workspace! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s explore your workspace where you can manage projects and workflows.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="workspace-header"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Workspace Overview
          </h3>
          <p style={contentStyles.text}>
            Your workspace contains all your projects and workflows. Create new projects or manage existing ones here.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="workspace-tabs"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Project Tabs
          </h3>
          <p style={contentStyles.text}>
            Switch between &quot;Recent&quot; to see your latest projects, &quot;Favourite&quot; to view starred projects, or &quot;All Projects&quot; to browse everything.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="workspace-views"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            View Options
          </h3>
          <p style={contentStyles.text}>
            Change how projects are displayed: Cards view for a visual grid, Tiles for compact cards, List for a detailed list, or Details for a comprehensive view with all information.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="workspace-project-grid"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Project Grid
          </h3>
          <p style={contentStyles.text}>
            Browse all your projects here. Click on any project card to view its details, workflows, and execution history. Hover over projects to see action buttons for editing or deleting.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="workspace-project-detail"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Project Detail View
          </h3>
          <p style={contentStyles.text}>
            When you select a project, you&apos;ll see its overview here including statistics like workflows count, transformations, sources, destinations, and last run status. This gives you a quick snapshot of your project&apos;s activity.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="workspace-workflows-table"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Workflows & Execution History
          </h3>
          <p style={contentStyles.text}>
            View all workflows and their execution history for the selected project. See run status, last execution time, source and destination details. Use action buttons to share, edit, or delete workflows.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="create-project"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Create Project
          </h3>
          <p style={contentStyles.text}>
            Click here to create a new project. Projects help you organize your workflows and pipelines.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
  ], [contentStyles]);

  const connectionsSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Connections! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s explore how to manage your data source connections and create new ones.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="connections-tabs"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Connection Tabs
          </h3>
          <p style={contentStyles.text}>
            Switch between &quot;My Connections&quot; to view your saved connections, or &quot;Connectors&quot; to browse available connector types.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="connections-filters"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Filter Connections
          </h3>
          <p style={contentStyles.text}>
            Use filters to find connections by connector type, owner, organization, environment, or creation date. Combine multiple filters for precise results.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="connections-search"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Search Connections
          </h3>
          <p style={contentStyles.text}>
            Click the search icon to quickly find connections by name or other details. The search works across all your connections.
          </p>
        </div>
      ),
      placement: 'left',
    },
    {
      target: '[data-tour="connections-table"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Connections Table
          </h3>
          <p style={contentStyles.text}>
            View all your connections in this table. Click on any connection to view details, or use the action buttons to test, edit, or delete connections.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-tour="create-connection"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Create New Connection
          </h3>
          <p style={contentStyles.text}>
            Click here to create a new connection. Set up connections to your data sources like Salesforce, databases, or APIs. These connections are secure and reusable across workflows.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
  ], [contentStyles]);

  const reportsSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Reports! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s explore the Reports page where you can view execution history, monitor pipeline runs, and track performance metrics.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="reports-filters"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Filter Options
          </h3>
          <p style={contentStyles.text}>
            Use these filters to customize your reports. Filter by date range, run mode, record type, and limit the number of results.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="reports-chart"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Workflow Breakdown Chart
          </h3>
          <p style={contentStyles.text}>
            This chart shows a visual breakdown of success and failure records across your workflows. Hover over bars to see detailed counts.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-tour="reports-table"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Workflow Run History
          </h3>
          <p style={contentStyles.text}>
            View detailed execution history in this table. Click on workflow names to see connections, or use the logs icon to view execution logs. You can also download the data as CSV.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ], [contentStyles]);

  const pipelineSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Pipeline Builder! 🎉
          </h3>
          <p style={contentStyles.text}>
            This is the no-code pipeline builder. Create custom ETL pipelines by dragging and dropping components without writing code.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="pipeline-title"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Pipeline Builder Overview
          </h3>
          <p style={contentStyles.text}>
            Here you can create custom ETL pipelines without writing code. Drag, drop, and deploy your data transformation workflows.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="pipeline-builder"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Pipeline Builder Interface
          </h3>
          <p style={contentStyles.text}>
            This is where you&apos;ll build your pipelines. Drag and drop components to create your data transformation workflows visually.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ], [contentStyles]);

  const announcementSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Announcements
          </h3>
          <p style={contentStyles.text}>
            Stay updated with important announcements, feature updates, and system notifications from your organization.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ], [contentStyles]);

  const helpSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Help Center! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s explore the Help Center to find answers, documentation, and support options.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="help-sidebar-search"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Search Help Content
          </h3>
          <p style={contentStyles.text}>
            Search for specific topics, questions, or keywords in the help documentation. Results will filter automatically as you type.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="help-ai-chat-sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            AI Chat Assistant
          </h3>
          <p style={contentStyles.text}>
            Click here to open the AI-powered chatbot. Get instant answers to your questions and receive guided assistance.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="help-contact-sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Contact Support
          </h3>
          <p style={contentStyles.text}>
            Need additional help? Contact our support team directly for personalized assistance with your questions or issues.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="help-main-search"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Main Search Bar
          </h3>
          <p style={contentStyles.text}>
            Search through all help content from the main area. This search works across all sections and FAQs.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="help-ai-chat-main"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            AI Assistant (Main Area)
          </h3>
          <p style={contentStyles.text}>
            Access the AI assistant from the main content area. Get instant help while browsing through help documentation.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-tour="help-contact-main"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Contact Support (Main Area)
          </h3>
          <p style={contentStyles.text}>
            Still need help? Contact our support team directly from here for personalized assistance.
          </p>
        </div>
      ),
      placement: 'top',
    },
  ], [contentStyles]);

  const adminSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Admin Panel! 🎉
          </h3>
          <p style={contentStyles.text}>
            Let&apos;s explore the Admin Panel where you can manage users, organizations, settings, and more.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="admin-tabs"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Admin Navigation Tabs
          </h3>
          <p style={contentStyles.text}>
            Use these tabs to navigate between different admin sections: User Management, Organizations, Announcements, Settings, and Audit Logs.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="admin-search"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Search Functionality
          </h3>
          <p style={contentStyles.text}>
            Use the search bar to quickly find items by name, email, or other details. The search filters results in real-time.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="admin-table"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Data Table
          </h3>
          <p style={contentStyles.text}>
            View all items in this table. Click on any item to view details, edit, or manage. You can also sort and filter the table.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="admin-create-user"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Create New Item
          </h3>
          <p style={contentStyles.text}>
            Click here to create a new item. Configure settings and assign permissions as needed.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
  ], [contentStyles]);

  const auditLogSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Audit Logs! 🎉
          </h3>
          <p style={contentStyles.text}>
            Track and monitor all system activities, user actions, and API calls. Use filters and export options to analyze audit data.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="admin-tabs"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Admin Navigation Tabs
          </h3>
          <p style={contentStyles.text}>
            Use these tabs to navigate between different admin sections: User Management, Organizations, Announcements, Settings, and Audit Logs.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="admin-search"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Search Audit Logs
          </h3>
          <p style={contentStyles.text}>
            Search through audit logs by typing keywords. The search filters results in real-time across all log entries.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="audit-filters"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Filter Options
          </h3>
          <p style={contentStyles.text}>
            Use date range pickers and filter dropdowns to narrow down audit logs by user, action, organization, email, and resource type. Filters work together to show precise results.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="audit-export"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Export to CSV
          </h3>
          <p style={contentStyles.text}>
            Export filtered audit logs to a CSV file for external analysis, reporting, or archival purposes. The export includes all currently visible filtered results.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="admin-table"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Audit Logs Table
          </h3>
          <p style={contentStyles.text}>
            View all audit log entries in this table. Each row shows details about user actions, API calls, timestamps, and resource changes. Use filters above to refine the view.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
  ], [contentStyles]);

  const adminSettingSteps = useMemo(() => [
    {
      target: 'body',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Welcome to Application Settings! 🎉
          </h3>
          <p style={contentStyles.text}>
            Configure application-wide settings including timezone, date formats, and workflow preferences for your organization.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: false,
    },
    {
      target: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Navigation Sidebar
          </h3>
          <p style={contentStyles.text}>
            Use the sidebar to navigate between different sections: Dashboard, Connections, Workspace, Reports, and more.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="admin-tabs"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Admin Navigation Tabs
          </h3>
          <p style={contentStyles.text}>
            Use these tabs to navigate between different admin sections: User Management, Organizations, Announcements, Settings, and Audit Logs.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="setting-timezone"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Default Timezone
          </h3>
          <p style={contentStyles.text}>
            Set the default timezone for your organization. This will be used across the application for displaying timestamps and scheduling workflows.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="setting-skip-secondary"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Skip Secondary Timezone
          </h3>
          <p style={contentStyles.text}>
            Toggle this switch to hide the secondary timezone option. When enabled, only the default timezone will be displayed throughout the application.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="setting-date-format"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Date Format
          </h3>
          <p style={contentStyles.text}>
            Choose how dates are displayed across the application. Select from various formats like YYYY-MM-DD, MM/DD/YYYY, or DD-MM-YYYY.
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="setting-save"]',
      content: (
        <div>
          <h3 style={contentStyles.title}>
            Save Changes
          </h3>
          <p style={contentStyles.text}>
            After configuring your settings, click this button to save all changes. Your timezone, date format, and other preferences will be applied organization-wide.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
  ], [contentStyles]);

  const getRouteKey = (pathname) => {
    if (pathname === '/userdashboard') return 'dashboard';
    if (pathname === '/recentworkflows') return 'workspace';
    if (pathname === '/myconnections') return 'connections';
    if (pathname === '/reports') return 'reports';
    if (pathname === '/pipeline') return 'pipeline';
    if (pathname === '/help') return 'help';
    if (pathname.startsWith('/admin')) return 'admin';
    return null;
  };

  const shouldShowTourForRoute = (pathname) => {
    console.log('[OnboardingTour] shouldShowTourForRoute check:', {
      pathname,
      enabled: tourSettings.enabled,
      isFirstTime: tourSettings.isFirstTime,
      routes: tourSettings.routes,
    });

    if (tourSettings.enabled === false) {
      console.log('[OnboardingTour] Tour explicitly disabled, returning false');
      return false;
    }

    if (tourSettings.isFirstTime) {
      console.log('[OnboardingTour] First time user, returning true');
      return true;
    }

    if (!tourSettings.enabled) {
      console.log('[OnboardingTour] Tour not enabled, returning false');
      return false;
    }

    const routeKey = getRouteKey(pathname);
    if (!routeKey) {
      console.log('[OnboardingTour] No route key found for:', pathname);
      return false;
    }

    const routeEnabled = tourSettings.routes[routeKey] === true;
    console.log('[OnboardingTour] Route check:', { routeKey, routeEnabled });
    return routeEnabled;
  };

  useEffect(() => {
    const loadTourSettings = () => {
      try {
        const savedTourSettings = localStorage.getItem('onboardingTourSettings');
        
        let onboardingEnabled = false;
        let onboardingRoutes = {
          dashboard: false,
          workspace: false,
          connections: false,
          reports: false,
          pipeline: false,
          help: false,
          admin: false,
        };
        let isFirstTime = true;

        if (savedTourSettings) {
          try {
            const parsed = JSON.parse(savedTourSettings);
            onboardingEnabled = parsed.onboardingTourEnabled || false;
            onboardingRoutes = parsed.onboardingTourRoutes || onboardingRoutes;
            isFirstTime = false;
          } catch (err) {
            console.warn('[OnboardingTour] Failed to parse saved settings', err);
            isFirstTime = true;
          }
        }

        console.log('[OnboardingTour] Settings loaded from localStorage:', {
          onboardingEnabled,
          isFirstTime,
          onboardingRoutes,
        });

        setTourSettings({
          enabled: onboardingEnabled,
          routes: onboardingRoutes,
          isFirstTime,
        });
      } catch (error) {
        console.error('[OnboardingTour] Error loading settings:', error);
        setTourSettings((prev) => ({ ...prev, isFirstTime: true }));
      } finally {
        setLoadingSettings(false);
      }
    };

    loadTourSettings();
  }, [location.pathname]);

  const getStepsForRoute = () => {
    console.log('[OnboardingTour] getStepsForRoute - pathname:', location.pathname);
    
    if (location.pathname === '/userdashboard') {
      console.log('[OnboardingTour] Returning steps for /userdashboard');
      return steps;
    }
    if (location.pathname === '/recentworkflows') {
      console.log('[OnboardingTour] Returning workspaceSteps for /recentworkflows');
      return workspaceSteps;
    }
    if (location.pathname === '/myconnections') {
      console.log('[OnboardingTour] Returning connectionsSteps for /myconnections');
      return connectionsSteps;
    }
    if (location.pathname === '/reports') {
      console.log('[OnboardingTour] Returning reportsSteps for /reports');
      return reportsSteps;
    }
    if (location.pathname === '/pipeline') {
      console.log('[OnboardingTour] Returning pipelineSteps for /pipeline');
      return pipelineSteps;
    }
    if (location.pathname === '/admin/annoucement' || location.pathname.startsWith('/admin/announcement')) {
      console.log('[OnboardingTour] Returning announcementSteps for', location.pathname);
      return announcementSteps;
    }
    if (location.pathname === '/admin/audit-log') {
      console.log('[OnboardingTour] Returning auditLogSteps for', location.pathname);
      return auditLogSteps;
    }
    if (location.pathname === '/admin/setting') {
      console.log('[OnboardingTour] Returning adminSettingSteps for', location.pathname);
      return adminSettingSteps;
    }
    if (location.pathname.startsWith('/admin')) {
      console.log('[OnboardingTour] Returning adminSteps for', location.pathname);
      console.log('[OnboardingTour] adminSteps:', adminSteps);
      return adminSteps;
    }
    if (location.pathname === '/help') {
      console.log('[OnboardingTour] Returning helpSteps for /help');
      return helpSteps;
    }
    console.log('[OnboardingTour] No steps found for pathname:', location.pathname);
    return [];
  };

  useEffect(() => {
    console.log('[OnboardingTour] 🔵🔵🔵 useEffect EXECUTING for pathname:', location.pathname);
    console.log('[OnboardingTour] ========== useEffect START ==========');
    console.log('[OnboardingTour] location.pathname:', location.pathname);
    
    const isAuthenticated = !!Cookies.get('authToken');
    const routesWithTour = [
      '/userdashboard',
      '/recentworkflows',
      '/myconnections',
      '/reports',
      '/pipeline',
      '/admin/organizations',
      '/admin/user',
      '/admin/setting',
      '/admin/audit-log',
      '/admin/annoucement',
      '/help',
    ];

    console.log('[OnboardingTour] isAuthenticated:', isAuthenticated);
    console.log('[OnboardingTour] routesWithTour:', routesWithTour);
    console.log('[OnboardingTour] routesWithTour.includes(pathname):', routesWithTour.includes(location.pathname));

    setRun(false);

    if (loadingSettings) {
      console.log('[OnboardingTour] Settings still loading, waiting...');
      return;
    }

    if (!isAuthenticated) {
      console.log('[OnboardingTour] ❌ User is not authenticated');
      return;
    }

    if (!routesWithTour.includes(location.pathname)) {
      console.log('[OnboardingTour] Route not in routesWithTour, skipping tour');
      return;
    }

    const shouldShow = shouldShowTourForRoute(location.pathname);
    console.log('[OnboardingTour] shouldShowTourForRoute:', shouldShow);
    console.log('[OnboardingTour] tourSettings:', tourSettings);

    if (!shouldShow) {
      console.log('[OnboardingTour] Tour disabled for this route based on settings');
      return;
    }

    console.log('[OnboardingTour] Route is in routesWithTour, getting steps...');
    const currentSteps = getStepsForRoute();
    console.log('[OnboardingTour] currentSteps:', currentSteps);
    console.log('[OnboardingTour] currentSteps.length:', currentSteps?.length);
    
    if (currentSteps && currentSteps.length > 0) {
      console.log('[OnboardingTour] Steps found, setting timer to start tour in 2000ms');
      
      let timerId = null;
      let isMounted = true;
      
      timerId = setTimeout(() => {
        if (isMounted) {
          console.log('[OnboardingTour] Timer fired, setting run to true');
          setRun(true);
        } else {
          console.log('[OnboardingTour] Timer fired but component unmounted, not setting run');
        }
      }, 2000);

      return () => {
        console.log('[OnboardingTour] 🟡🟡🟡 Cleanup: clearing timer and marking as unmounted for pathname:', location.pathname);
        isMounted = false;
        if (timerId) {
          clearTimeout(timerId);
        }
      };
    } else {
      console.warn('[OnboardingTour] No steps found or steps array is empty');
    }
  }, [location.pathname, loadingSettings, tourSettings]);

  const handleJoyrideCallback = async (data) => {
    const { status, action, index, type } = data;
    console.log('[OnboardingTour] handleJoyrideCallback:', { status, action, index, type });

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      console.log('[OnboardingTour] Tour finished or skipped, setting run to false');
      setRun(false);
      
      if (tourSettings.isFirstTime) {
        try {
          const tourSettingsToSave = {
            onboardingTourEnabled: false,
            onboardingTourRoutes: {
              dashboard: false,
              workspace: false,
              connections: false,
              reports: false,
              pipeline: false,
              help: false,
              admin: false,
            },
          };
          localStorage.setItem('onboardingTourSettings', JSON.stringify(tourSettingsToSave));
          setTourSettings((prev) => ({ ...prev, isFirstTime: false, enabled: false }));
        } catch (error) {
          console.error('[OnboardingTour] Error saving first-time completion:', error);
          setTourSettings((prev) => ({ ...prev, isFirstTime: false }));
        }
      }
    }

    if (status === STATUS.ERROR) {
      console.error('[OnboardingTour] Tour step error:', data);
    }

    if (status === STATUS.READY) {
      console.log('[OnboardingTour] Tour is ready to start');
    }

    if (status === STATUS.RUNNING) {
      console.log('[OnboardingTour] Tour is running, step index:', index);
    }
  };

  const startTour = () => {
    setRun(true);
  };

  React.useEffect(() => {
    window.startOnboardingTour = startTour;
    return () => {
      delete window.startOnboardingTour;
    };
  }, []);

  React.useEffect(() => {
    if (window.location.hash === '#start-tour' && !run) {
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [run]);

  const currentSteps = getStepsForRoute();
  console.log('[OnboardingTour] Render - currentSteps:', currentSteps);
  console.log('[OnboardingTour] Render - run state:', run);
  console.log('[OnboardingTour] Render - currentSteps.length:', currentSteps?.length);

  if (!currentSteps || currentSteps.length === 0) {
    console.log('[OnboardingTour] Render - No steps, returning null');
    return null;
  }

  const getResponsiveStyles = () => {
    const baseButtonStyles = {
      fontFamily: 'IBM Plex Sans, sans-serif',
      fontWeight: 500,
      textTransform: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      zIndex: 10001,
      pointerEvents: 'auto',
      position: 'relative',
    };

    if (isMobile) {
      return {
        tooltip: {
          borderRadius: '4px',
          padding: '20px 18px',
          fontFamily: 'IBM Plex Sans, sans-serif',
          maxWidth: 'calc(100vw - 24px)',
          width: 'auto',
          minWidth: '280px',
        },
        tooltipContainer: {
          textAlign: 'left',
          pointerEvents: 'auto',
        },
        overlay: {
          pointerEvents: 'auto',
        },
        buttonNext: {
          ...baseButtonStyles,
          backgroundColor: '#3E9663',
          fontSize: '0.75rem',
          padding: '0.375rem 0.75rem',
          minHeight: '32px',
          width: '100%',
        },
        buttonBack: {
          ...baseButtonStyles,
          color: '#525252',
          fontSize: '0.75rem',
          padding: '0.375rem 0.75rem',
          minHeight: '32px',
          width: '100%',
          marginRight: '0',
          marginBottom: '8px',
        },
        buttonSkip: {
          ...baseButtonStyles,
          color: '#6B7280',
          fontSize: '0.75rem',
          padding: '0.375rem 0.75rem',
          minHeight: '32px',
          width: '100%',
        },
        tooltipFooter: {
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        },
      };
    }

    if (isTablet) {
      return {
        tooltip: {
          borderRadius: '4px',
          padding: '16px 18px',
          fontFamily: 'IBM Plex Sans, sans-serif',
          maxWidth: '90%',
        },
        tooltipContainer: {
          textAlign: 'left',
          pointerEvents: 'auto',
        },
        overlay: {
          pointerEvents: 'auto',
        },
        buttonNext: {
          ...baseButtonStyles,
          backgroundColor: '#3E9663',
          fontSize: '0.8125rem',
          padding: '0.4375rem 0.8125rem',
          minHeight: '34px',
        },
        buttonBack: {
          ...baseButtonStyles,
          color: '#525252',
          fontSize: '0.8125rem',
          padding: '0.4375rem 0.8125rem',
          minHeight: '34px',
          marginRight: '9px',
        },
        buttonSkip: {
          ...baseButtonStyles,
          color: '#6B7280',
          fontSize: '0.8125rem',
          padding: '0.4375rem 0.8125rem',
          minHeight: '34px',
        },
        tooltipFooter: {
          pointerEvents: 'auto',
        },
      };
    }

    return {
      tooltip: {
        borderRadius: '4px',
        padding: '20px',
        fontFamily: 'IBM Plex Sans, sans-serif',
      },
      tooltipContainer: {
        textAlign: 'left',
        pointerEvents: 'auto',
      },
      overlay: {
        pointerEvents: 'auto',
      },
      buttonNext: {
        ...baseButtonStyles,
        backgroundColor: '#3E9663',
        fontSize: '0.875rem',
        padding: '0.5rem 0.875rem',
        minHeight: '36px',
      },
      buttonBack: {
        ...baseButtonStyles,
        color: '#525252',
        fontSize: '0.875rem',
        padding: '0.5rem 0.875rem',
        minHeight: '36px',
        marginRight: '10px',
      },
      buttonSkip: {
        ...baseButtonStyles,
        color: '#6B7280',
        fontSize: '0.875rem',
        padding: '0.5rem 0.875rem',
        minHeight: '36px',
      },
      tooltipFooter: {
        pointerEvents: 'auto',
      },
    };
  };

  return (
    <Joyride
      steps={currentSteps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling={false}
      disableOverlayClose={true}
      spotlightClicks={false}
      hideCloseButton={true}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0056D2',
          textColor: '#383838',
          backgroundColor: '#FFFFFF',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#FFFFFF',
          zIndex: 10000,
        },
        ...getResponsiveStyles(),
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
};

export default OnboardingTour;
